/*
 * THE NAVIGATION, as data. Today plus four areas, grouped by the part of the
 * business a page serves rather than by how often it is opened — agreed with
 * Gabriel 2026-09-30 and drawn in Claude Design (Navigation.dc.html).
 *
 *   Inbound   the moment a lead arrives: which page or form, what score, the
 *             first reply
 *   Sales     once it is a company being worked
 *   Meetings  anything about time
 *   Reports   what happened
 *
 * The sidebar and the phone's tab bar both read this file, so the two frames
 * cannot disagree about where a page lives.
 *
 * ONLY PAGES THAT EXIST ARE LISTED. (`soon` still draws an item greyed out
 * and unlinked; Landing pages used it until its page was built.) The
 * mockup also draws People, Forms, Attribution and Activity. Each
 * is added here in the change that builds its page; a menu item that opens a
 * placeholder is a promise the app is not keeping.
 *
 * URLS DID NOT CHANGE. The grouping is a property of the menu, not of the
 * address: "/" is still the board and "/follow-ups" still Follow-ups, so every
 * link already sitting in Slack or an email still lands. The only new
 * addresses are the per-area Automations lists.
 */

// Which area an automation belongs to, by its trigger type. An automation lives
// beside the thing it serves: the after-hours reply is the inbound response,
// reminders and the no-show rescue are part of meetings.
export const AREA_OF_TYPE = {
  after_hours_auto_reply: "inbound",
  call_reminders: "meetings",
  call_no_show: "meetings",
  call_ended: "meetings",
  lead_goes_stale: "sales",
  company_stage_changed: "sales",
};

export const AREA_LABEL = { sales: "Sales", meetings: "Meetings", inbound: "Inbound", reports: "Reports" };

/*
 * Each child: key (unique within its area), label, the address it opens, an
 * optional badge name, and `match` — the addresses that count as being on it.
 * A match entry is a path, or a path prefix ending in "/", or a [path, tab]
 * pair for the booking admin, whose two menu items are two tabs of one page.
 *
 * Sales has no Automations entry yet: neither of its trigger types (lead goes
 * stale, stage changed) can be created today, so the list would always be empty.
 */
export const AREAS = [
  {
    id: "sales",
    label: "Sales",
    glyph: "briefcase",
    items: [
      { key: "pipeline", label: "Pipeline", to: "/", match: ["/"] },
      { key: "leads", label: "Leads", to: "/leads", match: ["/leads"] },
      { key: "followups", label: "Follow-ups", to: "/follow-ups", badge: "followups", match: ["/follow-ups", "/follow-ups/"] },
      { key: "approvals", label: "Approvals", to: "/approvals", badge: "pending", match: ["/approvals"] },
      // Groups of people (src/lists): All Qualified Contacts, No Contact, hand-made ones.
      { key: "lists", label: "Lists", to: "/lists", match: ["/lists", "/lists/"] },
      // One private page per prospect with what he chose to share (src/rooms), and
      // the decks and examples those pages pick from.
      { key: "rooms", label: "Prospect rooms", to: "/sales/rooms", match: ["/sales/rooms"] },
      { key: "library", label: "Decks & examples", to: "/sales/library", match: ["/sales/library"] },
    ],
    // A contact's record is a Sales page even though no menu item names it: the
    // area stays open, and nothing inside it is highlighted.
    also: ["/contacts/"],
  },
  {
    id: "meetings",
    label: "Meetings",
    glyph: "calendar",
    items: [
      // No badge: a booked call needs nothing from him, so a count of them
      // would be a number asking for attention it does not need.
      { key: "calendar", label: "Calendar", to: "/meetings/calendar", match: ["/meetings/calendar"] },
      // Every call Fireflies recorded, filed under its company (src/calls).
      { key: "calls", label: "Calls", to: "/meetings/calls", match: ["/meetings/calls", "/meetings/calls/"] },
      { key: "bookings", label: "Bookings", to: "/booking-page", match: [["/booking-page", "bookings"]] },
      { key: "bookingpage", label: "Booking page", to: "/booking-page?tab=page", match: [["/booking-page", "page"], ["/booking-page", "log"]] },
      { key: "availability", label: "Availability", to: "/meetings/availability", match: ["/meetings/availability"] },
      { key: "automations", label: "Automations", to: "/meetings/automations", match: ["/meetings/automations"] },
    ],
  },
  {
    id: "inbound",
    label: "Inbound",
    glyph: "inbox",
    items: [
      // The pages lp.remotion.io serves (src/landing).
      { key: "landing", label: "Landing pages", to: "/inbound/landing-pages", match: ["/inbound/landing-pages"] },
      // remotion.io, the landing pages and the booking page: live, and over a range.
      { key: "analytics", label: "Analytics", to: "/inbound/analytics", match: ["/inbound/analytics"] },
      { key: "scoring", label: "Scoring", to: "/inbound/scoring", match: ["/inbound/scoring"] },
      { key: "automations", label: "Automations", to: "/inbound/automations", match: ["/inbound/automations"] },
    ],
  },
  {
    id: "reports",
    label: "Reports",
    glyph: "barChart",
    items: [{ key: "dashboard", label: "Dashboard", to: "/reports", match: ["/reports"] }],
  },
];

function matches(entry, pathname, tab) {
  if (Array.isArray(entry)) {
    const [path, wanted] = entry;
    // The booking admin opens on Bookings when no tab is named.
    return pathname === path && (tab || "bookings") === wanted;
  }
  if (entry.endsWith("/") && entry !== "/") return pathname.startsWith(entry);
  return pathname === entry;
}

/*
 * Where am I? Returns { area, item } — `area` is an area id, "today" or
 * "settings", or null for a page outside the menu; `item` is the child's key
 * or null.
 *
 * `hint` is the answer a page gave for itself when the address alone cannot
 * say: an automation's record is at /automations/:id, and only the record
 * knows its trigger type.
 */
export function locate(pathname, search, hint) {
  if (hint) {
    const [area, item] = hint.split("/");
    return { area, item: item || null };
  }
  if (pathname === "/today") return { area: "today", item: null };
  if (pathname === "/settings") return { area: "settings", item: null };
  const tab = new URLSearchParams(search).get("tab");
  for (const area of AREAS) {
    for (const item of area.items) {
      if (item.match.some((m) => matches(m, pathname, tab))) return { area: area.id, item: item.key };
    }
    if ((area.also || []).some((m) => matches(m, pathname, tab))) return { area: area.id, item: null };
  }
  return { area: null, item: null };
}

/** The sum of an area's badges — what a closed area shows. */
export function areaBadge(area, badges) {
  return area.items.reduce((n, item) => n + ((item.badge && badges[item.badge]) || 0), 0);
}
